import { useCallback, useEffect, useState } from "react";
import { fetchFoodChainBranchLocatorStrategy } from "../../../../api/foodApi";
import AdminTableContainer from "../layout/AdminTableContainer";

const statusClass = (status) => {
  if (!status) return "";
  if (status.startsWith("APPROVED")) return "badge badge--success";
  if (status === "DO_NOT_USE") return "badge badge--danger";
  if (status === "NEEDS_REVIEW" || status === "MANUAL_CURATION_ONLY") {
    return "badge badge--warning";
  }
  return "badge";
};

const FoodChainBranchLocatorStrategyPage = ({ onClose }) => {
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const loadStrategy = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const payload = await fetchFoodChainBranchLocatorStrategy();
      setStrategy(payload);
    } catch (error) {
      setStrategy(null);
      setErrorMessage(error.message || "Unable to load chain branch locator strategy.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStrategy();
  }, [loadStrategy]);

  return (
    <div className="admin-page food-chain-locator-strategy-page">
      <header className="admin-page__header">
        <div>
          <h1>Chain Branch Locator Strategy</h1>
          <p className="muted">
            Planning and governance for supermarket branch imports. Read-only — no upload, geocoding,
            or ingest in this milestone.
          </p>
        </div>
        <div className="admin-page__header-actions">
          <button type="button" className="btn btn-secondary" onClick={loadStrategy} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          {onClose ? (
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Back
            </button>
          ) : null}
        </div>
      </header>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}
      {loading && !strategy ? <p className="muted">Loading strategy…</p> : null}

      {strategy ? (
        <>
          <section className="panel-card">
            <h2>Scope</h2>
            <div className="food-chain-locator-strategy-page__kpi-row">
              <div>
                <span className="muted">Target area</span>
                <strong>{strategy.targetArea}</strong>
              </div>
              <div>
                <span className="muted">Import allowed</span>
                <strong>{strategy.importAllowed ? "Yes" : "No"}</strong>
              </div>
              <div>
                <span className="muted">Upload allowed</span>
                <strong>{strategy.uploadAllowed ? "Yes" : "No"}</strong>
              </div>
              <div>
                <span className="muted">Strategy doc</span>
                <strong>{strategy.strategyDocPath}</strong>
              </div>
            </div>
            {strategy.pocNote ? <p className="muted food-chain-locator-strategy-page__hint">{strategy.pocNote}</p> : null}
            {strategy.retailPriceWarning ? (
              <p className="muted food-chain-locator-strategy-page__hint">{strategy.retailPriceWarning}</p>
            ) : null}
          </section>

          <section className="panel-card">
            <h2>Chain source plans</h2>
            <AdminTableContainer>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Chain</th>
                    <th>Locator key</th>
                    <th>Planned source</th>
                    <th>Legal/ops</th>
                    <th>Retail price link</th>
                    <th>Next action</th>
                  </tr>
                </thead>
                <tbody>
                  {(strategy.chainPlans || []).map((plan) => (
                    <tr key={plan.locatorSourceKey}>
                      <td>{plan.chainBrand}</td>
                      <td><code>{plan.locatorSourceKey}</code></td>
                      <td>{plan.preferredSourceType}</td>
                      <td>
                        <span className={statusClass(plan.legalOpsStatus)}>{plan.legalOpsStatus}</span>
                      </td>
                      <td><code>{plan.retailPriceSourceKey}</code></td>
                      <td>{plan.nextAction}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableContainer>
          </section>

          <section className="panel-card">
            <h2>Import format (Milestone 5)</h2>
            <p className="muted">
              Templates: {strategy.templatePaths?.csvTemplate}, {strategy.templatePaths?.jsonTemplate}
            </p>
            <div className="food-chain-locator-strategy-page__columns">
              <div>
                <strong>Required</strong>
                <ul>
                  {(strategy.importFormat?.requiredFields || []).map((field) => (
                    <li key={field}><code>{field}</code></li>
                  ))}
                </ul>
              </div>
              <div>
                <strong>Recommended</strong>
                <ul>
                  {(strategy.importFormat?.recommendedFields || []).slice(0, 6).map((field) => (
                    <li key={field}><code>{field}</code></li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className="panel-card">
            <h2>Geocoding strategy</h2>
            <ol>
              {(strategy.geocodeStrategy?.preferredOrder || []).map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <p className="muted">
              Status values: {(strategy.geocodeStrategy?.statusValues || []).join(", ")}
            </p>
          </section>

          <section className="panel-card">
            <h2>Legal/ops checklist (initial)</h2>
            <AdminTableContainer>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Chain</th>
                    <th>Source type</th>
                    <th>Review status</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(strategy.legalOpsChecklist || []).map((row) => (
                    <tr key={row.chainBrand}>
                      <td>{row.chainBrand}</td>
                      <td>{row.dataSourceType}</td>
                      <td>
                        <span className={statusClass(row.reviewStatus)}>{row.reviewStatus}</span>
                      </td>
                      <td>{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableContainer>
          </section>

          <section className="panel-card">
            <h2>Milestone 5 tasks</h2>
            <ul>
              {(strategy.milestone5Tasks || []).map((task) => (
                <li key={task}>{task}</li>
              ))}
            </ul>
          </section>
        </>
      ) : null}
    </div>
  );
};

export default FoodChainBranchLocatorStrategyPage;
